import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AlertTriangle, ThumbsDown, ThumbsUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './AIMessage.module.css';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
// Chart components
import { EmbeddedTradingViewChart } from '../chart/EmbeddedTradingViewChart';
import { EodChart } from '../chart/2DChart';
import { Surface3DChart } from '../chart/3DChart';
import { RegressionChart } from '../chart/RegressionChart';
import { ChartService, ChartConfig, ToolDataResponse, VolSurfaceToolDataResponse, VolSurface3DData, RegressionData, getToolDataValues } from '../../../services/ChartService';
import { useAuth } from '../../../contexts/AuthContext';
import { SaveDataPopover } from './SaveDataPopover';
import { ExportCsvPopover } from './ExportCsvPopover';
import { csvExportService } from '../../../services/CsvExportService';
import { BacktestProgressBar, BacktestPhase } from './BacktestProgressBar';
import { BacktestResultsChart } from '../backtest-analytics/BacktestResultsChart';
import { LegData, BacktestSeriesPayload, hasChartableBacktestResults } from '../../utils/returns';
import logger from '../../../logging/logger';
import { MessageFeedback } from '../../../services/SessionService';

/**
 * Prepare vol surface data for 3D chart rendering
 * Transforms fetched data into the format expected by Surface3DChart
 */
function prepareVolSurfaceData(
  data: VolSurfaceToolDataResponse,
  axisType: 'strike' | 'delta'
): VolSurface3DData {
  // Select the appropriate axis and MidIV matrix based on axis_type
  if (axisType === 'strike') {
    return {
      DTE: data.DTE,
      Strike: data.Strike,
      MidIV: data.StrikeMidIV,
    };
  } else {
    return {
      DTE: data.DTE,
      Delta: data.Delta,
      MidIV: data.DeltaMidIV,
    };
  }
}

interface BacktestProgressData {
  progress: number;
  phase: BacktestPhase;
  currentPeriod?: string;
  currentSymbol?: string;
  isComplete?: boolean;
  error?: string;
}

// Backtest result reference stored in message content (fetched from API)
interface BacktestResultReference {
  results_data_id: string;
  backtest_config_id: string;
}

interface AIMessageProps {
  messageId: string;
  text: string;
  feedback?: MessageFeedback | null;
  onFeedbackChange?: (messageId: string, feedback: MessageFeedback | null) => Promise<boolean>;
  onDownloadExcel?: () => void;
  messageContent?: {
    text?: string;
    chart?: any;
    embedded_chart?: any;
    data?: any;
    strategy_config?: any;
    executed_code?: string[]; // Python code executed via execute_python tool
    new_data_ids?: Record<string, string>; // data_id -> description for save feature
    backtest_progress?: BacktestProgressData; // Backtest progress bar data
    backtest_results?: LegData | BacktestSeriesPayload; // Backtest results for chart rendering
    backtest_result?: BacktestResultReference; // Backtest result reference for persisted messages
  };
  sessionId?: string; // Added for chart retrieval
}

export const AIMessage: React.FC<AIMessageProps> = ({
  messageId,
  text,
  feedback = null,
  onFeedbackChange,
  onDownloadExcel: _onDownloadExcel,
  messageContent,
  sessionId
}) => {
  const [chartConfig, setChartConfig] = useState<ChartConfig | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [toolData, setToolData] = useState<ToolDataResponse | null>(null);
  const [isLoadingToolData, setIsLoadingToolData] = useState(false);
  const [volSurfaceData, setVolSurfaceData] = useState<VolSurfaceToolDataResponse | null>(null);
  const [isLoadingVolSurfaceData, setIsLoadingVolSurfaceData] = useState(false);
  const [regressionData, setRegressionData] = useState<RegressionData | null>(null);
  const [isLoadingRegressionData, setIsLoadingRegressionData] = useState(false);
  const [showExecutedCode, setShowExecutedCode] = useState(false);
  const [showSavePopover, setShowSavePopover] = useState(false);
  const [showExportPopover, setShowExportPopover] = useState(false);
  const [isDeveloperTier, setIsDeveloperTier] = useState(false);
  const [tierCheckComplete, setTierCheckComplete] = useState(false);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<MessageFeedback | null>(feedback ?? null);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);

  // State for fetched backtest results (from backtest_result reference)
  const [fetchedBacktestResults, setFetchedBacktestResults] = useState<(LegData | BacktestSeriesPayload) | null>(null);
  const [isLoadingBacktestResults, setIsLoadingBacktestResults] = useState(false);
  const [backtestResultsError, setBacktestResultsError] = useState<string | null>(null);

  const { user, userUuid, getToken, isAuthenticated } = useAuth();

  // Chart service instance for type checking and API calls
  const chartService = useMemo(() => new ChartService(), []);

  // Check if we have embedded chart data in the message content
  const embeddedChart = useMemo(() => messageContent?.embedded_chart, [messageContent?.embedded_chart]);
  const hasEmbeddedChart = Boolean(embeddedChart);
  const rawText = messageContent?.text || text || '';
  const displayText = typeof rawText === 'string'
    ? rawText
        .replace(/<br\s*\/?>/gi, '\n')
        // Ensure markdown headings have a blank line before them so react-markdown parses them correctly
        // Only match # at line start (after \n) to avoid false positives like "F# language"
        .replace(/\n(#{1,6}\s)/g, '\n\n$1')
    : rawText;

  // Check if we have executed code from execute_python tool
  const executedCode = useMemo(() => messageContent?.executed_code, [messageContent?.executed_code]);
  const hasExecutedCode = Boolean(executedCode && executedCode.length > 0);

  // Check if we have new data_ids for the save feature
  const newDataIds = useMemo(() => messageContent?.new_data_ids, [messageContent?.new_data_ids]);
  const hasNewDataIds = Boolean(newDataIds && Object.keys(newDataIds).length > 0);

  // Check developer tier status (cached) - only show CSV export for developer tier
  useEffect(() => {
    const checkDeveloperTier = async () => {
      if (!userUuid || !isAuthenticated) {
        setIsDeveloperTier(false);
        setTierCheckComplete(true);
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          setIsDeveloperTier(false);
          setTierCheckComplete(true);
          return;
        }

        const isDev = await csvExportService.isDeveloperTier(userUuid, token);
        logger.info('AIMessage: Developer tier check result:', isDev);
        setIsDeveloperTier(isDev);
      } catch (error) {
        logger.error('AIMessage: Failed to check developer tier:', error);
        setIsDeveloperTier(false);
      } finally {
        setTierCheckComplete(true);
      }
    };

    checkDeveloperTier();
  }, [userUuid, isAuthenticated, getToken]);

  // Only show export button if tier check is complete AND user is developer tier
  const showExportCsvButton = tierCheckComplete && isDeveloperTier && hasNewDataIds;

  useEffect(() => {
    setSelectedFeedback(feedback ?? null);
  }, [feedback]);


  useEffect(() => {
    Prism.highlightAll();
  }, [displayText, showExecutedCode]);

  // Load chart configuration when embedded chart is present
  useEffect(() => {
    const loadChartConfig = async () => {
      if (!hasEmbeddedChart) return;
      
      // For direct chart configs (like TradingView with symbol), we don't need sessionId
      if (chartService.isDirectChartConfig(embeddedChart)) {
        logger.info('AIMessage: Using direct chart config (TradingView)');
        setChartConfig(embeddedChart);
        setIsLoadingChart(false);
        return;
      }
      
      if (!sessionId || !user?.email || !isAuthenticated) return;

      try {
        setIsLoadingChart(true);
        setChartError(null);

        // Chart reference (new format) - needs API fetch
        if (chartService.isValidChartReference(embeddedChart)) {
          const chartRef = embeddedChart;
          logger.info('AIMessage: Loading chart reference', chartRef);

          // Try to get from embedded config first (immediate use)
          if (chartRef.config) {
            setChartConfig(chartRef.config);
          } else {
            // Fetch from session storage
            const token = await getToken();
            if (!token) {
              setChartError('Authentication token not available');
              return;
            }
            const config = await chartService.getChartConfig(sessionId, chartRef.chart_id, user.email, token);
            if (config) {
              setChartConfig(config);
            } else {
              setChartError('Chart configuration not found');
            }
          }
        } else {
          // This shouldn't happen - direct configs are handled above
          logger.warn('AIMessage: Invalid embedded chart format', embeddedChart);
          setChartError('Invalid chart format');
        }
      } catch (error) {
        logger.error('AIMessage: Failed to load chart configuration', error);
        setChartError('Failed to load chart');
      } finally {
        setIsLoadingChart(false);
      }
    };

    loadChartConfig();
  }, [hasEmbeddedChart, sessionId, user?.email, embeddedChart, getToken, isAuthenticated, chartService]);

  // Fetch tool data when chartConfig has data_id
  useEffect(() => {
    const loadToolData = async () => {
      if (!chartConfig || !sessionId || !user?.email || !isAuthenticated) return;

      // Check if this is a Custom2D chart that needs data fetching
      if (chartService.isCustom2DChart(chartConfig) && chartService.needsDataFetch(chartConfig)) {
        try {
          setIsLoadingToolData(true);
          const token = await getToken();
          if (!token) {
            setChartError('Authentication token not available');
            return;
          }

          // data_id is always a list
          const dataIds = chartConfig.data_id!;
          const seriesLabels = chartConfig.series_labels;
          const dataSources = await chartService.getMultipleToolData(sessionId, dataIds, seriesLabels, user.email, token);
          if (dataSources.length > 0) {
            // Merge data by date for rendering (works for single or multi-trace)
            const mergedData = chartService.mergeDataByDate(dataSources);
            setToolData(mergedData);
          } else {
            setChartError('No chart data found');
          }
        } catch (error) {
          logger.error('AIMessage: Failed to load tool data', error);
          setChartError('Failed to load chart data');
        } finally {
          setIsLoadingToolData(false);
        }
      }

      // Check if this is a 3D vol surface chart with data_id
      if (chartService.isVolSurface3DChart(chartConfig) && chartService.isVolSurface3DDataIdChart(chartConfig)) {
        const dataId = chartConfig.data_id;
        if (!dataId) return;

        try {
          setIsLoadingVolSurfaceData(true);
          const token = await getToken();
          if (!token) {
            setChartError('Authentication token not available');
            return;
          }

          const data = await chartService.getVolSurfaceData(sessionId, dataId, user.email, token);
          if (data) {
            setVolSurfaceData(data);
          } else {
            setChartError('Vol surface data not found');
          }
        } catch (error) {
          logger.error('AIMessage: Failed to load vol surface data', error);
          setChartError('Failed to load 3D chart data');
        } finally {
          setIsLoadingVolSurfaceData(false);
        }
      }

      // Check if this is a Regression chart with data_id
      if (chartService.isRegressionChart(chartConfig)) {
        const dataId = chartConfig.data_id;
        if (!dataId) return;

        try {
          setIsLoadingRegressionData(true);
          const token = await getToken();
          if (!token) {
            setChartError('Authentication token not available');
            return;
          }

          const data = await chartService.getRegressionData(sessionId, dataId, user.email, token);
          if (data) {
            setRegressionData(data);
          } else {
            setChartError('Regression data not found');
          }
        } catch (error) {
          logger.error('AIMessage: Failed to load regression data', error);
          setChartError('Failed to load regression chart data');
        } finally {
          setIsLoadingRegressionData(false);
        }
      }
    };

    loadToolData();
  }, [chartConfig, sessionId, user?.email, getToken, isAuthenticated, chartService]);

  // Fetch backtest results when we have a backtest_result reference (persisted messages)
  const backtestResultRef = messageContent?.backtest_result;
  useEffect(() => {
    const loadBacktestResults = async () => {
      if (!backtestResultRef?.results_data_id || !sessionId || !user?.email || !isAuthenticated) return;

      try {
        setIsLoadingBacktestResults(true);
        setBacktestResultsError(null);

        const token = await getToken();
        if (!token) {
          setBacktestResultsError('Authentication token not available');
          return;
        }

        // Fetch the results from tool_data using results_data_id
        const data = await chartService.getToolData(sessionId, backtestResultRef.results_data_id, user.email, token);
        if (data) {
          // The tool_output contains the actual LegData results
          setFetchedBacktestResults(data as unknown as (LegData | BacktestSeriesPayload));
          logger.info('AIMessage: Loaded backtest results from reference', backtestResultRef.results_data_id);
        } else {
          setBacktestResultsError('Backtest results not found');
        }
      } catch (error) {
        logger.error('AIMessage: Failed to load backtest results', error);
        setBacktestResultsError('Failed to load backtest results');
      } finally {
        setIsLoadingBacktestResults(false);
      }
    };

    loadBacktestResults();
  }, [backtestResultRef?.results_data_id, sessionId, user?.email, getToken, isAuthenticated, chartService]);


  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const copyEntireMessage = () => {
    const textToCopy = typeof displayText === 'string' ? displayText : '';
    navigator.clipboard.writeText(textToCopy);
  };

  const handleFeedbackClick = async (target: MessageFeedback) => {
    const nextFeedback: MessageFeedback | null = selectedFeedback === target ? null : target;
    const previousFeedback = selectedFeedback;
    setSelectedFeedback(nextFeedback);

    if (!onFeedbackChange || !messageId) {
      return;
    }

    setIsSavingFeedback(true);
    try {
      const result = await onFeedbackChange(messageId, nextFeedback);
      if (result === false) {
        setSelectedFeedback(previousFeedback);
      }
    } catch (_error) {
      setSelectedFeedback(previousFeedback);
    } finally {
      setIsSavingFeedback(false);
    }
  };

  // Check if this is a backtest progress message
  const backtestProgress = messageContent?.backtest_progress;
  const backtestResults = messageContent?.backtest_results;
  const isBacktestProgressMessage = !!backtestProgress;
  const hasBacktestResults = hasChartableBacktestResults(backtestResults);

  // Check if we have fetched backtest results from a reference
  const hasFetchedBacktestResults = hasChartableBacktestResults(fetchedBacktestResults);

  // Render backtest progress bar and results chart (no copy button, no other content)
  if (isBacktestProgressMessage) {
    return (
      <div className={styles.aiMessage}>
        <BacktestProgressBar
          progress={backtestProgress.progress}
          phase={backtestProgress.phase}
          currentPeriod={backtestProgress.currentPeriod}
          currentSymbol={backtestProgress.currentSymbol}
          isComplete={backtestProgress.isComplete}
          error={backtestProgress.error}
        />
        {/* Show chart below progress bar when backtest is complete with results */}
        {hasBacktestResults && backtestProgress.isComplete && (
          <BacktestResultsChart
            data={backtestResults}
            height="400px"
          />
        )}
      </div>
    );
  }

  // Check if we have a backtest_result reference (persisted message with results to fetch)
  const hasBacktestResultRef = !!backtestResultRef?.results_data_id;

  if ((!displayText || typeof displayText !== 'string' || displayText.trim() === '') && !hasEmbeddedChart && !hasBacktestResultRef) {
    return null;
  }

  // Render backtest results from persisted reference (fetched via results_data_id)
  if (hasBacktestResultRef && !displayText && !hasEmbeddedChart) {
    return (
      <div className={styles.aiMessage}>
        {isLoadingBacktestResults && (
          <div className={styles.chartLoading}>Loading backtest results...</div>
        )}
        {backtestResultsError && (
          <div className={styles.chartError}>
            <span className={styles.errorContent}>
              <AlertTriangle className={styles.errorIcon} aria-hidden="true" />
              {backtestResultsError}
            </span>
          </div>
        )}
        {hasFetchedBacktestResults && !isLoadingBacktestResults && (
          <>
            <BacktestProgressBar
              progress={100}
              phase="complete"
              isComplete={true}
            />
            <BacktestResultsChart
              data={fetchedBacktestResults}
              height="400px"
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.aiMessage}>
      {displayText && typeof displayText === 'string' && displayText.trim() && (
        <div className={styles.content}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Headers
              h1: ({ children }) => <h1 className={styles.h1}>{children}</h1>,
              h2: ({ children }) => <h2 className={styles.h2}>{children}</h2>,
              h3: ({ children }) => <h3 className={styles.h3}>{children}</h3>,
              h4: ({ children }) => <h4 className={styles.h4}>{children}</h4>,
              h5: ({ children }) => <h5 className={styles.h4}>{children}</h5>,
              h6: ({ children }) => <h6 className={styles.h4}>{children}</h6>,
              
              // Paragraphs
              p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
              
              // Lists
              ul: ({ children }) => <ul className={styles.bulletList}>{children}</ul>,
              ol: ({ children, start }) => (
                <ol className={styles.numberedList} start={start}>
                  {children}
                </ol>
              ),
              li: ({ children, ...props }) => {
                // Check if parent is ol or ul based on ordered prop
                const isOrdered = (props as any).ordered;
                return (
                  <li className={isOrdered ? styles.numberedItem : styles.bulletItem}>
                    {children}
                  </li>
                );
              },
              
              // Code blocks
              code: ({ className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || '');
                const language = match ? match[1] : '';
                const isInline = !className;
                
                if (isInline) {
                  return <code className={styles.inlineCode}>{children}</code>;
                }
                
                const code = String(children).replace(/\n$/, '');
                const languageMap: { [key: string]: string } = {
                  'js': 'javascript', 'ts': 'typescript', 'py': 'python',
                  'sh': 'bash', 'shell': 'bash', 'rs': 'rust'
                };
                const prismLanguage = languageMap[language] || language || 'text';
                
                return (
                  <div className={styles.codeBlock}>
                    <div className={styles.codeHeader}>
                      <span className={styles.language}>{language || 'code'}</span>
                      <button className={styles.copyButton} onClick={() => copyToClipboard(code)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Copy code
                      </button>
                    </div>
                    <pre className={styles.codeContent}>
                      <code className={`language-${prismLanguage}`}>{code}</code>
                    </pre>
                  </div>
                );
              },
              
              // Prevent wrapping code blocks in pre
              pre: ({ children }) => <>{children}</>,
              
              // Links
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
                  {children}
                </a>
              ),
              
              // Horizontal rule
              hr: () => <hr className={styles.horizontalRule} />,
              
              // Strong/Bold
              strong: ({ children }) => <strong>{children}</strong>,
              
              // Emphasis/Italic
              em: ({ children }) => <em>{children}</em>,
              
              // Blockquote
              blockquote: ({ children }) => (
                <blockquote className={styles.blockquote}>{children}</blockquote>
              ),
              
              // Tables
              table: ({ children }) => (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>{children}</table>
                </div>
              ),
              th: ({ children }) => <th className={styles.tableHeader}>{children}</th>,
              td: ({ children }) => <td className={styles.tableCell}>{children}</td>,
            }}
          >
            {displayText}
          </ReactMarkdown>
        </div>
      )}
      
      {/* Render embedded chart if available */}
      {hasEmbeddedChart && (
        <div className={styles.chartSection}>
          {isLoadingChart && (
            <div className={styles.chartLoading}>
              <div className={styles.loadingSpinner}></div>
              <span>Loading chart...</span>
            </div>
          )}

          {chartError && (
            <div className={styles.chartError}>
              <span className={styles.errorContent}>
                <AlertTriangle className={styles.errorIcon} aria-hidden="true" />
                {chartError}
              </span>
            </div>
          )}

          {chartConfig && !isLoadingChart && (
            <>
              {/* Render Custom 2D Chart for analytics/time series data */}
              {chartService.isCustom2DChart(chartConfig) && (
                <>
                  {/* Context optimized: data_id or data_ids reference - wait for tool data */}
                  {chartService.needsDataFetch(chartConfig) && (
                    <>
                      {isLoadingToolData && (
                        <div className={styles.chartLoading}>
                          <div className={styles.loadingSpinner}></div>
                          <span>Loading chart data...</span>
                        </div>
                      )}
                      {toolData && !isLoadingToolData && (() => {
                        // Build chart data - for multi-trace, toolData already has Dates and series columns
                        const chartData = toolData.Dates
                          ? { ...toolData, Description: chartConfig.title }
                          : {
                              Dates: toolData.dates,
                              [chartConfig.value_label || 'Value']: getToolDataValues(toolData),
                              Description: chartConfig.title
                            };
                        logger.info('AIMessage: Passing to EodChart', chartData);
                        return (
                          <EodChart
                            data={chartData}
                            height="400px"
                            valueLabel={chartConfig.value_label}
                            referenceLines={chartConfig.reference_lines}
                          />
                        );
                      })()}
                    </>
                  )}
                  {/* Legacy: embedded data */}
                  {!chartService.needsDataFetch(chartConfig) && chartConfig.data && (
                    <EodChart
                      data={{
                        ...chartConfig.data,
                        Description: chartConfig.title
                      }}
                      height="400px"
                    />
                  )}
                </>
              )}

              {/* Render TradingView Chart for symbol-based charts */}
              {chartService.isTradingViewChart(chartConfig) && (
                <EmbeddedTradingViewChart
                  symbol={chartConfig.symbol}
                  comparison_symbols={chartConfig.comparison_symbols}
                  timeframe={chartConfig.timeframe}
                  // theme is intentionally omitted - chart will follow app's theme from ThemeContext
                  chart_type={chartConfig.chart_type}
                  indicators={chartConfig.indicators}
                />
              )}

              {/* Render 3D Volatility Surface Chart */}
              {chartService.isVolSurface3DChart(chartConfig) && (
                <>
                  {/* Loading state for data_id charts */}
                  {chartService.isVolSurface3DDataIdChart(chartConfig) && isLoadingVolSurfaceData && (
                    <div className={styles.chartLoading}>Loading 3D surface data...</div>
                  )}
                  {/* Render with fetched data (data_id) */}
                  {chartService.isVolSurface3DDataIdChart(chartConfig) && volSurfaceData && (
                    <Surface3DChart
                      data={prepareVolSurfaceData(volSurfaceData, chartConfig.axis_type)}
                      height="500px"
                    />
                  )}
                  {/* Render with embedded data (legacy) */}
                  {!chartService.isVolSurface3DDataIdChart(chartConfig) && chartConfig.data && (
                    <Surface3DChart
                      data={chartConfig.data}
                      height="500px"
                    />
                  )}
                </>
              )}

              {/* Render Regression Chart */}
              {chartService.isRegressionChart(chartConfig) && (
                <>
                  {isLoadingRegressionData && (
                    <div className={styles.chartLoading}>Loading regression data...</div>
                  )}
                  {regressionData && (
                    <RegressionChart
                      title={chartConfig.title}
                      data={regressionData}
                      height="500px"
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
      
      <div className={styles.messageActions}>
        {/* Hide copy button for backtest_result notification messages */}
        {!messageContent?.backtest_result && (
          <button
            className={styles.copyMessageButton}
            onClick={copyEntireMessage}
            title="Copy message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            Copy
          </button>
        )}

        {!messageContent?.backtest_result && (
          <>
            <button
              className={`${styles.feedbackButton} ${selectedFeedback === 'thumbs_up' ? styles.feedbackButtonActive : ''}`}
              onClick={() => handleFeedbackClick('thumbs_up')}
              title="Thumbs up"
              disabled={isSavingFeedback}
              aria-pressed={selectedFeedback === 'thumbs_up'}
              aria-label="Thumbs up"
            >
              <ThumbsUp
                size={16}
                fill={selectedFeedback === 'thumbs_up' ? 'currentColor' : 'none'}
              />
            </button>
            <button
              className={`${styles.feedbackButton} ${selectedFeedback === 'thumbs_down' ? styles.feedbackButtonActive : ''}`}
              onClick={() => handleFeedbackClick('thumbs_down')}
              title="Thumbs down"
              disabled={isSavingFeedback}
              aria-pressed={selectedFeedback === 'thumbs_down'}
              aria-label="Thumbs down"
            >
              <ThumbsDown
                size={16}
                fill={selectedFeedback === 'thumbs_down' ? 'currentColor' : 'none'}
              />
            </button>
          </>
        )}

        {/* Code toggle button - only shown when executed_code is present */}
        {hasExecutedCode && (
          <button
            className={`${styles.codeToggleButton} ${showExecutedCode ? styles.codeToggleActive : ''}`}
            onClick={() => setShowExecutedCode(!showExecutedCode)}
            title={showExecutedCode ? 'Hide executed code' : 'Show executed code'}
          >
            &lt;/Code&gt;
          </button>
        )}

        {/* Save button - only shown when new_data_ids is present */}
        {hasNewDataIds && (
          <button
            ref={saveButtonRef}
            className={`${styles.saveDataButton} ${showSavePopover ? styles.saveDataButtonActive : ''}`}
            onClick={() => setShowSavePopover(!showSavePopover)}
            title="Save data"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            Save
          </button>
        )}

        {/* Export CSV button - only shown for developer tier when new_data_ids is present */}
        {showExportCsvButton && (
          <button
            ref={exportButtonRef}
            className={`${styles.exportCsvButton} ${showExportPopover ? styles.exportCsvButtonActive : ''}`}
            onClick={() => setShowExportPopover(!showExportPopover)}
            title="Export to CSV (Developer)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            CSV
          </button>
        )}
      </div>

      {/* Save Data Popover */}
      {showSavePopover && hasNewDataIds && (
        <SaveDataPopover
          newDataIds={newDataIds!}
          onClose={() => setShowSavePopover(false)}
          anchorRef={saveButtonRef}
          onSaveSuccess={(qtsName, action) => {
            logger.info(`✅ Saved as ${qtsName} (${action})`);
          }}
        />
      )}

      {/* Export CSV Popover - Developer tier only */}
      {showExportPopover && showExportCsvButton && sessionId && (
        <ExportCsvPopover
          dataIds={newDataIds!}
          onClose={() => setShowExportPopover(false)}
          anchorRef={exportButtonRef}
          sessionId={sessionId}
          onExportSuccess={(filename) => {
            logger.info(`✅ Exported as ${filename}`);
          }}
        />
      )}

      {hasExecutedCode && showExecutedCode && (
        <div className={styles.executedCodeSection}>
          {executedCode!.map((code, index) => (
            <div key={`executed-code-${index}`} className={styles.codeBlock}>
              <div className={styles.codeHeader}>
                <span className={styles.language}>python</span>
                <button className={styles.copyButton} onClick={() => copyToClipboard(code)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Copy code
                </button>
              </div>
              <pre className={styles.codeContent}>
                <code className="language-python">{code}</code>
              </pre>
            </div>
          ))}
        </div>
      )}

      {/* Render fetched backtest results from persisted message reference */}
      {backtestResultRef && (
        <div className={styles.chartSection}>
          {isLoadingBacktestResults && (
            <div className={styles.chartLoading}>
              <div className={styles.loadingSpinner}></div>
              <span>Loading backtest results...</span>
            </div>
          )}
          {backtestResultsError && (
            <div className={styles.chartError}>
              <span className={styles.errorContent}>
                <AlertTriangle className={styles.errorIcon} aria-hidden="true" />
                {backtestResultsError}
              </span>
            </div>
          )}
          {hasFetchedBacktestResults && !isLoadingBacktestResults && (
            <>
              <BacktestProgressBar
                progress={100}
                phase="complete"
                isComplete={true}
              />
              <BacktestResultsChart
                data={fetchedBacktestResults}
                height="400px"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};
